import {
  ajax,
  getLanguageISO
} from 'application/src/js/functions.js';

/**
 * @class
 * @param {number} args.rawmaterialid
 * @param {object} args.colors
 * @param {function} args.drawLimitLines
 * @param {function} args.showTooltip
 * @param {object} args.translations
 * @param {Mediator} args.mediator
 */
export default class CropHistoryChart {
  constructor(args) {
    this.rawmaterialid = args.rawmaterialid;
    this.colors = args.colors;
    this.drawLimitLines = args.drawLimitLines;
    this.showTooltip = args.showTooltip;
    this.translations = args.translations;
    this.mediator = args.mediator;

    this.container = document.querySelector('.lp-content .crop-history');
    this.legend = this.container.querySelector('.chart-legend');
    this.chartContainer = this.container.querySelector('.chart-container');
    this.chartCanvas = this.container.querySelector('.chart');
    this.verticalLineCanvas = this.container.querySelector('.vertical-line');

    this.requestUrl = '/app/ajax/ajaxLoadRelatedCropHistory';
    this.debugMode = true;
    this.hideClass = 'hide';
    this.chart = null;
    this.minimizedChart = window.innerWidth < 800;
    this.showAmount = 6;
    this.showTooltipTimeout = null;

    this.load()
      .then(response => {
        this.user = response.user;
        this.rawData = response.data;
        this.datasets = this.createDataSets();
        this.buildLegend();
        this.initChart();
        this.addEvents();
        if (Object.keys(this.rawData).length) this.container.classList.remove(this.hideClass);

        this.mediator.publish('finishedLoading');
      })
      .catch(e => {
        if (this.debugMode && e) console.log(e);
        this.mediator.publish('finishedLoading');
      });
  }

  load() {
    this.mediator.publish('startedLoading');

    return ajax(this.requestUrl, {
      rawmaterialid: this.rawmaterialid,
    });
  }

  shrinkChart() {
    this.chart.options.layout.padding.right = 0;
    this.chart.options.scales.xAxes.map(axis => axis.ticks.maxTicksLimit = 6);
    this.chart.update();
  }

  enlargeChart() {
    this.chart.options.layout.padding.right = 110;
    this.chart.options.scales.xAxes.map(axis => axis.ticks.maxTicksLimit = 12);
    this.chart.update();
  }

  addEvents() {
    this.chartCanvas.addEventListener('mousemove', e => this.drawVerticalLine(e));

    window.addEventListener('resize', e => {
      if (window.innerWidth < 800 && !this.minimizedChart) {
        this.minimizedChart = true;
        this.shrinkChart();
      }

      if (window.innerWidth > 800 && this.minimizedChart) {
        this.minimizedChart = false;
        this.enlargeChart();
      }
    });
  }

  initChart() {
    const chartSettings = {
      type: 'line',
      data: {
        labels: Object.keys(this.rawData).map(year => parseInt(year)),
        datasets: this.datasets
      },
      plugins: [{
        beforeDraw: _ => {
          this.drawLimitLines(this.chart, this.minimizedChart, this.translations, 0);

          // if the mouse cursor is outside the canvas, show the last tooltip after 1000ms
          if (this.showTooltipTimeout) clearTimeout(this.showTooltipTimeout);

          this.showTooltipTimeout = setTimeout(_ => this.showTooltip(this.chart, this.chart.data.datasets[0].data.length - 1), 1000);
        },
        resize: _ => this.drawLimitLines(this.chart, this.minimizedChart, this.translations, 0)
      }],
      options: {
        layout: {
          padding: {
            left: 0,
            right: this.minimizedChart ? 0 : 110,
            top: 0,
            bottom: 0
          }
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            axis: 'x',
            callbacks: {
              label : context => new Intl.NumberFormat(getLanguageISO()).format(context.raw)
            }
          }
        },
        hover: {
          mode: 'nearest',
          intersect: false,
          axis: 'x',
        },
        scales: {
          x: {
            gridLines: {
              drawBorder: false,
              drawOnChartArea: true,
              tickMarkLength: 0,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
            },
            ticks: {
              maxTicksLimit: this.minimizedChart ? 6 : 12,
              lineHeight: 1,
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              padding: 10,
            },
          },
          y: {
            gridLines: {
              drawBorder: true,
              drawOnChartArea: true,
              tickMarkLength: 0
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 7,
              padding: 5,
              callback: (label, index, labels) => label
            },
            title: {
              display: true,
              text: 'mt',
              padding: 0
            },
            padding: this.minimizedChart ? 0 : 20,
          },
        },
        responsive: true,
        elements: {
          line: {
            tension: 0,
            borderWidth: 1
          },
          point: {
            radius: 0,
            hitRadius: 5,
            hoverRadius: 3,
          }
        }
      }
    };

    this.chart = new Chart(this.chartCanvas, chartSettings);
  }

  createDataSets() {
    const amountsPerCountry = Object.keys(this.rawData).reduce((summed, year) => {
      Object.keys(this.rawData[year]).map(countryShort => {
        const t = parseFloat(this.rawData[year][countryShort]);
        if (summed[countryShort]) {
          summed[countryShort].sum += t;
          summed[countryShort].chartData.push({
            x: t,
            y: parseInt(year)
          });
        } else {
          summed[countryShort] = {
            country: countryShort,
            sum: t,
            chartData: [{
              x: t,
              y: parseInt(year)
            }]
          };
        }
      })

      return summed;
    }, {});

    const topProducers = Object.values(amountsPerCountry).sort((a, b) => a.sum < b.sum ? 1 : -1).slice(0, this.showAmount);

    return topProducers.reduce((data, current, i) => {
      data.push({
        borderColor: this.colors[i],
        pointBackgroundColor: this.colors[i],
        pointBorderColor: this.colors[i],
        label: `${current.country}`,
        data: current.chartData.map(d => d.x),
        backgroundColor: 'transparent'
      });

      return data;
    }, []);
  }

  drawVerticalLine(e) {
    this.verticalLineCanvas.width = this.chartCanvas.width;
    this.verticalLineCanvas.height = this.chartCanvas.height;
    const offsetLeft = this.chartContainer.getBoundingClientRect().left;
    const clientX = parseInt(e.clientX - offsetLeft);
    const ctx = this.verticalLineCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.verticalLineCanvas.width, this.verticalLineCanvas.height);
    ctx.beginPath();
    ctx.moveTo((clientX + 0.5) * window.devicePixelRatio, this.chart.chartArea.top);
    ctx.lineTo((clientX + 0.5) * window.devicePixelRatio, this.chart.chartArea.bottom);
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0, 113, 188, 0.75)';
    ctx.stroke();
  }

  buildLegend() {
    let tpl = ``;

    this.datasets.map(set => {
      tpl += `<div style="border-color:${set.pointBorderColor}; color:${set.pointBorderColor}">${set.label}</div>`;
    });

    this.legend.insertAdjacentHTML('beforeend', tpl);
  }
}